import React, { useState } from 'react';
import { openExternalUrl } from '../../../services/webviewService';
import { ProductDetailModal } from './ProductDetailModal';
import {
  ProductActions,
  ProductCopy,
  ProductFeedback,
  ProductMedia,
  ProductSummary,
  ProductVariants,
  useProductCart,
} from './product';

interface ProductListProps {
  products: Array<ProductSummary & { link: string }>;
  explanation?: string;
  type: 'product_list' | 'product_grid';
  onProductClick: (productId: number) => void;
  agent_chat_id?: number;
}

interface ProductItemProps {
  product: ProductSummary & { link: string };
  onProductClick: (productId: number) => void;
  agentChatId?: number;
  layout: 'tile' | 'row';
}

const ProductItem: React.FC<ProductItemProps> = ({ product, onProductClick, agentChatId, layout }) => {
  const [showDetailModal, setShowDetailModal] = useState(false);
  const cart = useProductCart(product.product_id, { agentChatId });

  const openProduct = () => {
    onProductClick(product.product_id);
    openExternalUrl(product.link);
  };

  const cartPanel = cart.showVariants && cart.variantOptions.length > 0 && (
    <div className="border-t border-ds-100 p-2.5">
      <p className="mb-2 text-[10px] font-bold text-ds-700">انتخاب گزینه محصول</p>
      <ProductVariants options={cart.variantOptions} selectedId={cart.selectedVariantId} onSelect={cart.setSelectedVariantId} />
      <button
        type="button"
        onClick={() => cart.addToCart()}
        disabled={cart.addingToCart || !cart.selectedVariantId}
        className="mt-2 h-9 w-full rounded-ds-6 bg-ds-primary text-xs font-bold text-white hover:bg-ds-primary-strong disabled:opacity-50"
      >
        {cart.addingToCart ? 'در حال افزودن...' : 'تأیید و افزودن'}
      </button>
      <ProductFeedback message={cart.message} />
    </div>
  );

  if (layout === 'tile') {
    return (
      <article className="group flex w-[164px] shrink-0 snap-start flex-col overflow-hidden rounded-ds-16 border border-ds-100 bg-ds-white transition-[border-color,box-shadow,transform] hover:-translate-y-0.5 hover:border-ds-200 hover:shadow-ds-2 app-sm:w-[184px]">
        <button type="button" onClick={openProduct} className="block text-right">
          <ProductMedia product={product} />
          <div className="flex min-h-[180px] flex-col p-3 app-sm:min-h-[194px] app-sm:p-4">
            <ProductCopy product={product} price={cart.selectedPrice} />
          </div>
        </button>
        {!cart.showVariants && (
          <div className="px-3 pb-3 app-sm:px-4 app-sm:pb-4">
            <ProductActions
              onAdd={cart.requestAddToCart}
              onDetail={() => setShowDetailModal(true)}
              loading={cart.loadingVariants || cart.addingToCart}
            />
            <ProductFeedback message={cart.message} />
          </div>
        )}
        {cartPanel}
        <ProductDetailModal productId={product.product_id} isOpen={showDetailModal} onClose={() => setShowDetailModal(false)} agent_chat_id={agentChatId} />
      </article>
    );
  }

  return (
    <article className="group overflow-hidden rounded-ds-12 border border-ds-100 bg-ds-white transition-[border-color,box-shadow] hover:border-ds-200 hover:shadow-ds-1">
      <div className="flex items-stretch gap-2.5 p-2.5 app-sm:p-3">
        <button type="button" onClick={openProduct} className="flex min-w-0 flex-1 gap-2.5 text-right">
          <ProductMedia product={product} className="h-20 w-20 shrink-0 rounded-ds-8" />
          <ProductCopy product={product} price={cart.selectedPrice} compact />
        </button>
        {!cart.showVariants && (
          <div className="hidden w-32 shrink-0 flex-col justify-center app-sm:flex">
            <ProductActions onAdd={cart.requestAddToCart} onDetail={() => setShowDetailModal(true)} loading={cart.loadingVariants || cart.addingToCart} addLabel="افزودن" />
            <ProductFeedback message={cart.message} />
          </div>
        )}
      </div>
      {!cart.showVariants && (
        <div className="border-t border-ds-100 p-2 app-sm:hidden">
          <ProductActions onAdd={cart.requestAddToCart} onDetail={() => setShowDetailModal(true)} loading={cart.loadingVariants || cart.addingToCart} />
          <ProductFeedback message={cart.message} />
        </div>
      )}
      {cartPanel}
      <ProductDetailModal productId={product.product_id} isOpen={showDetailModal} onClose={() => setShowDetailModal(false)} agent_chat_id={agentChatId} />
    </article>
  );
};

export const ProductList: React.FC<ProductListProps> = ({ products, explanation, type, onProductClick, agent_chat_id }) => (
  <div className="w-full">
    {explanation && <p className="mb-3 text-[13px] leading-7 text-ds-700">{explanation}</p>}
    <div className={type === 'product_grid' ? 'ds-product-strip' : 'space-y-2'}>
      {products?.map((product) => (
        <ProductItem
          key={product.product_id}
          product={product}
          onProductClick={onProductClick}
          agentChatId={agent_chat_id}
          layout={type === 'product_grid' ? 'tile' : 'row'}
        />
      ))}
    </div>
  </div>
);
